/**
 * Shared provider-access rules for scheduling & provider dropdowns.
 *
 * Single source of truth consolidating the legacy Dashboard
 * `getAccessibleProviders` semantics so the V2 calendar dialog,
 * the legacy dialog hook, and the treatment plan provider dropdown
 * all agree (Oct 2026 — admin-role users were being treated as
 * providers in V2 contexts).
 */
import { isSuperAdmin } from './helpers';

/**
 * True when the user can see/schedule every provider in the practice:
 * super admin, admin, or office roles.
 */
export const hasOrgWideProviderAccess = (user, userRoleName) => {
  if (!user) return false;
  const role = (userRoleName ?? user.role ?? '').toLowerCase();
  return isSuperAdmin(user) || role === 'admin' || role === 'office';
};

/**
 * Role-filtered provider list for the Add Appointment dialog.
 *  • org-wide users (super admin / admin / office): all active providers
 *    for billable types (client_session, group); all active staff for
 *    non-billable time.
 *  • provider-role users: themselves + their supervisees (active
 *    providers whose supervisor_ids include the user).
 *  • other roles: themselves for non-billable personal time (mirrors
 *    legacy fallbacks so dropdowns never come up empty for edge roles).
 */
export const getAccessibleProvidersShared = ({ user, userRoleName, users, providers, appointmentType = null }) => {
  if (!user) return [];
  const requiresProvider = appointmentType === 'client_session' || appointmentType === 'group';
  const staffList = (Array.isArray(users) && users.length ? users : providers) || [];
  const activeStaff = staffList.filter((u) => u && u.is_active !== false);

  if (hasOrgWideProviderAccess(user, userRoleName)) {
    if (requiresProvider) return activeStaff.filter((u) => u.is_provider === true);
    return activeStaff;
  }

  const role = (userRoleName ?? user.role ?? '').toLowerCase();
  // 'supervisor' included to match the backend _can_schedule_for_provider
  // grant — a supervisor without is_provider still books for supervisees.
  if (role === 'provider' || role === 'supervisor' || user?.is_provider) {
    const supervisees = activeStaff.filter(
      (u) => u.id !== user.id && u.supervisor_ids && u.supervisor_ids.includes(user.id) && u.is_provider === true
    );
    // Non-provider supervisors can't BE the provider on a billable
    // session — only their supervisees can.
    const includeSelf = user?.is_provider || !requiresProvider;
    return includeSelf ? [user, ...supervisees] : supervisees;
  }

  // Edge roles (e.g. non-provider staff booking their own block time).
  if (!requiresProvider) return [user];
  return [];
};
